import { useEffect, useState } from 'react';
import type { AiModelPrice, GcpBillingConfig } from '@jordan/types';
import {
  Alert,
  Breadcrumb,
  Button,
  Input,
  PageHeader,
  SettingsPanel,
  SettingsRow,
  Spinner,
} from '@jordan/ui';
import { AppChrome } from '../components/AppChrome';
import { apiGet, apiPut } from '../lib/api';

const PROVIDER_LABEL: Record<AiModelPrice['provider'], string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google Gemini',
  bedrock: 'Bedrock',
};

export function BillingSettingsPage() {
  const [table, setTable] = useState<string | null>(null); // null = loading
  const [prices, setPrices] = useState<AiModelPrice[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [pricesBusy, setPricesBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<GcpBillingConfig>('/api/billing/gcp/config')
      .then((c) => setTable(c.table))
      .catch(() => setError('Failed to load billing settings.'));
    apiGet<AiModelPrice[]>('/api/billing/ai-prices')
      .then(setPrices)
      .catch(() => undefined);
  }, []);

  const saveTable = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const c = await apiPut<GcpBillingConfig>('/api/billing/gcp/config', { table: (table ?? '').trim() });
      setTable(c.table);
      setMessage('Google Cloud billing settings saved.');
    } catch {
      setError('Could not save — expected the form project.dataset.table.');
    } finally {
      setBusy(false);
    }
  };

  const setPrice = (model: string, field: 'input' | 'output', value: string) => {
    setPrices((prev) =>
      prev ? prev.map((p) => (p.model === model ? { ...p, [field]: value === '' ? 0 : Number(value) } : p)) : prev,
    );
  };

  const savePrices = async () => {
    if (!prices) return;
    setPricesBusy(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await apiPut<AiModelPrice[]>('/api/billing/ai-prices', { prices });
      setPrices(saved);
      setMessage('AI model prices saved.');
    } catch {
      setError('Could not save AI model prices — values must be non-negative numbers.');
    } finally {
      setPricesBusy(false);
    }
  };

  return (
    <AppChrome>
      <PageHeader
        title={
          <>
            Configuration - <span className="fw-normal">Billing</span>
          </>
        }
        breadcrumb={
          <Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Configuration' }, { label: 'Billing' }]} />
        }
      />

      <div className="content">
        {message && (
          <Alert variant="success" icon="ph-check-circle" dismissible>
            {message}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" icon="ph-warning" dismissible>
            {error}
          </Alert>
        )}

        {table === null ? (
          <Spinner />
        ) : (
          <SettingsPanel
            title="Google Cloud"
            description="Connect your Google Cloud Billing export so the Billing dashboard can report costs."
          >
            <SettingsRow
              title="BigQuery export table"
              description="Cost data comes from your Cloud Billing BigQuery export. Enable the “Standard usage cost” export in the Google Cloud console, grant this app’s service account BigQuery read access, then paste the export table here."
            >
              <div className="d-flex align-items-start gap-2" style={{ maxWidth: 560 }}>
                <div className="flex-grow-1">
                  <Input
                    value={table}
                    onChange={(e) => setTable(e.target.value)}
                    placeholder="my-project.billing_export.gcp_billing_export_v1_XXXXXX_XXXXXX_XXXXXX"
                  />
                </div>
                <Button variant="primary" disabled={busy} onClick={saveTable}>
                  Save
                </Button>
              </div>
            </SettingsRow>
          </SettingsPanel>
        )}

        <SettingsPanel
          title="AI usage pricing"
          description="Anthropic, OpenAI and Google Gemini spend is self-tracked from usage that runs through Jordan’s AI chat, priced with the per-model rates below (USD per 1,000,000 tokens). These reflect only usage routed through Jordan, not your full provider bill."
        >
          {prices === null ? (
            <Spinner />
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-bordered align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Provider</th>
                      <th>Model</th>
                      <th className="text-end" style={{ width: 180 }}>
                        Input $/1M
                      </th>
                      <th className="text-end" style={{ width: 180 }}>
                        Output $/1M
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {prices.map((p) => (
                      <tr key={p.model}>
                        <td>{PROVIDER_LABEL[p.provider]}</td>
                        <td>
                          {p.label}
                          <div className="text-muted fs-sm">
                            <code>{p.model}</code>
                          </div>
                        </td>
                        <td>
                          <Input
                            type="number"
                            min={0}
                            step="0.01"
                            className="text-end"
                            value={String(p.input)}
                            onChange={(e) => setPrice(p.model, 'input', e.target.value)}
                          />
                        </td>
                        <td>
                          <Input
                            type="number"
                            min={0}
                            step="0.01"
                            className="text-end"
                            value={String(p.output)}
                            onChange={(e) => setPrice(p.model, 'output', e.target.value)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="d-flex justify-content-end mt-3">
                <Button variant="primary" disabled={pricesBusy} onClick={savePrices}>
                  Save prices
                </Button>
              </div>
            </>
          )}
        </SettingsPanel>
      </div>
    </AppChrome>
  );
}
